import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';
import { Alert, AlertOptions } from '@bazis/shared/models/alert.types';

/**
 * **BazisAlertService**
 *
 * Central service for creating and dismissing application alerts.
 * Stores the last created `Alert` instance and exposes it as an Observable
 * so UI components can react to alert changes.
 */
@Injectable({
    providedIn: 'root',
})
export class BazisAlertService {
    /**
     * Default options used for every created alert.
     * Incoming options provided to `create()` are merged on top of these values.
     */
    private _defaultParams: AlertOptions = {
        messageKey: '',
        headerKey: '',
        buttons: [
            {
                titleKey: 'ok',
            },
        ],
        hasCloseIcon: true,
        closeIcon: 'cross-small',
    };

    /**
     * Last created alert instance.
     * Set by `create()` and cleared by `dismiss()`.
     */
    private _alert: Alert;

    /**
     * Internal update trigger for `alert$` stream.
     * Emits whenever `_alert` is replaced or dismissed.
     */
    private _needUpdateAlerts$ = new BehaviorSubject(true);

    /**
     * Observable that emits the current alert instance.
     *
     * Note: the value may be `null` when there is no active alert.
     */
    alert$ = this._needUpdateAlerts$.asObservable().pipe(map(() => this._alert));

    /**
     * Create a new alert and publish it through `alert$`.
     * The provided `params` are merged with `_defaultParams`.
     *
     * `Alert.onDidDismiss()` returns a Promise that resolves when `dismiss()` is called.
     *
     * @param params - Alert options to apply. Expected shape: `Partial<AlertOptions>`.
     * @returns Newly created `Alert` instance.
     */
    create(params) {
        params = {
            ...this._defaultParams,
            ...params,
        };

        const alert: Alert = {
            options: params,
            onDidDismiss: () => {
                return new Promise((resolve) => {
                    alert.dismiss = resolve;
                });
            },
        };

        this._alert = alert;

        this._needUpdateAlerts$.next(true);
        return alert;
    }

    /**
     * Dismiss an alert and notify subscribers.
     *
     * If `alert` is not provided, dismisses the current `_alert`.
     * When there is no alert to dismiss, the method returns immediately.
     *
     * @param result - Value passed to the alert `dismiss()` callback. Use `null` when no result is needed.
     * @param alert - Specific alert instance to dismiss. Default: current `_alert`.
     * @returns void
     */
    dismiss(result = null, alert = this._alert) {
        if (!alert) return;

        alert.dismiss(result);
        this._alert = null;
        this._needUpdateAlerts$.next(true);
    }
}
